import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	Input,
	OnDestroy,
	OnInit,
	signal,
} from '@angular/core';
import { base64 } from '@core/util/file';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select, Store } from '@ngxs/store';
import { WordDocumentStyles } from '@services/docx-document/docx-document.models';
import { DocxDocumentService } from '@services/docx-document/docx-document.service';
import { BusinessState } from '@state/business';
import { BusinessBaseDocxsState } from '@state/business/word/business-base-docxs/business-base-docxs.state';
import { TagsState } from '@state/global/tags/tags.state';
import { MatterWordDocumentsDispatch } from '@state/matter/matter-word-documents/matter-word-documents.dispatch';
import { MatterWordDocumentWithMetadata } from '@state/matter/matter-word-documents/matter-word-documents.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { DocumentsDrawerSelectors } from 'src/app/private/sale/documents-v2/documents-drawer/documents-drawer.selectors';
import { TestHooks } from './documents-word-matter-viewer.hooks';
import { UploadService } from '../../services/file/upload.service';
import { MatterPackageContentDocumentType } from '@models/matter-package';
import { Logger } from '../../core/logger';
import { DocumentCategory } from '@models/matter-word-documents';

@Component({
	selector: 'app-documents-word-matter-viewer',
	templateUrl: 'documents-word-matter-viewer.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DocumentsWordMatterViewerComponent implements OnInit, OnDestroy {
	testHooks = TestHooks;
	private inputChange$ = new BehaviorSubject<string>(null);
	private destroy$ = new Subject<void>();
	usingSplitView = input<boolean>(false);
	private uploadService = inject(UploadService);

	@Input() set sourceId(sourceId: string) {
		this._sourceId = sourceId;
		this.inputChange$.next(sourceId);
	}

	get sourceId() {
		return this._sourceId;
	}

	get documentId() {
		return this._documentId;
	}

	@Input() set documentId(docId: string) {
		this._documentId = docId;
		this.loading_.set(true);
		this.inputChange$.next(docId);
	}

	@Input() showControls = true;

	public documentWithMetadata_ = signal<MatterWordDocumentWithMetadata>(null);
	private _sourceId: string;
	private _documentId: string;
	@SelectSnapshot(BusinessState.getBusinessWordDocumentStyles)
	wordDocumentStyles: WordDocumentStyles;
	@SelectSnapshot(BusinessBaseDocxsState.getBaseDocxPathForOffice)
	getBaseDocxPathForOfficeFn: (officeId: string, referenceId: string) => string;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(BusinessState.businessId) businessId: string;
	@SelectSnapshot(SelectedMatter.officeId)
	officeId: string;

	@Select(TagsState.getLastUpdateStore)
	tagsLastUpdateStore$: Observable<number>;

	matterDocuments$ = this.store.select(
		DocumentsDrawerSelectors.getAllVirtualAndMatterDocuments,
	);

	public pdfBase64: base64;
	public blob: Blob;
	public fileName = 'file.pdf';
	public loading_ = signal(true);
	public isPrinting_ = signal(false);
	public isDocx_ = computed(
		() => this.documentMimeType_() === MatterPackageContentDocumentType.WORD,
	);
	public isPDF_ = computed(
		() => this.documentMimeType_() === MatterPackageContentDocumentType.PDF,
	);
	private documentMimeType_ = computed(() => {
		return {
			pdf: MatterPackageContentDocumentType.PDF,
			docx: MatterPackageContentDocumentType.WORD,
		}[
			this.documentWithMetadata_()?.document.sourceStorageRef.match(
				/\.(pdf|docx)$/,
			)?.[1]
		];
	});

	ngOnInit(): void {
		combineLatest({
			docs: this.matterDocuments$,
			tagsUpdate: this.tagsLastUpdateStore$,
			input: this.inputChange$,
		})
			.pipe(debounceTime(1000), takeUntil(this.destroy$))
			.subscribe(async ({ docs }) => {
				this.loading_.set(true);
				const document = docs.find(
					(d) =>
						(d.document.source && d.document.source === this.sourceId) ||
						d.document.id === this.documentId,
				);

				if (!document) {
					this.loading_.set(false);
					return;
				}

				this.documentWithMetadata_.set(document);

				if (
					document.document.sourceStorageRef?.includes('.docx') ||
					document.document.categories?.includes(DocumentCategory.SYNC_FORMS) ||
					document.document.categories?.includes(DocumentCategory.MATTER_FORMS)
				) {
					const { base64 } = await this.docxDocumentService.getDocumentAsPdf(
						document.id,
					);
					this.blob = null;
					this.pdfBase64 = base64;
					this.fileName = document.document.title + '.pdf';
				} else {
					this.blob = await this.uploadService.getBlob(
						document.document.sourceStorageRef,
					);
					this.pdfBase64 = null;
					this.fileName = document.document.title;
				}

				this.loading_.set(false);
			});
	}

	constructor(
		private store: Store,
		private matterWordDocumentsDispatch: MatterWordDocumentsDispatch,
		private docxDocumentService: DocxDocumentService,
	) {}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		this.inputChange$.complete();
	}

	openInWord() {
		this.documentWithMetadata_.update((prev) => ({ ...prev, isOpening: true }));
		const document = this.documentWithMetadata_().document;
		if (!this.documentWithMetadata_().isGenerated) {
			this.matterWordDocumentsDispatch.Create({
				document,
				autoOpen: true,
			});
		} else {
			this.matterWordDocumentsDispatch.OpenDocumentInWord(document);
		}
	}

	async print() {
		if (!this.documentWithMetadata_) return;

		this.isPrinting_.set(true);

		try {
			await this.docxDocumentService.printDocument(
				this.documentWithMetadata_().id,
			);
		} catch (error) {
			Logger.exception(error);
		} finally {
			this.isPrinting_.set(false);
		}
	}
	async export() {
		if (!this.documentWithMetadata_) return;

		this.documentWithMetadata_.update((prev) => ({
			...prev,
			isExporting: true,
		}));

		try {
			await this.docxDocumentService.exportDocument(
				this.documentWithMetadata_().id,
			);
		} catch (error) {
			Logger.exception(error);
		} finally {
			this.documentWithMetadata_.update((prev) => ({
				...prev,
				isExporting: false,
			}));
		}
	}
}
